import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlayerPickerDialog from "../fanPage/PlayerPickerDialog";
import PlayerPickRow from "../fanPage/PlayerPickRow";
import { useGetNbaPositionLeadersQuery, useSearchNbaPlayersQuery } from "../../store/api/nbaApi";
import { useGetNflPositionLeadersQuery, useSearchNflPlayersQuery } from "../../store/api/nflApi";
import { formatLeaderMeta, playerSearchName } from "../../utils/community";

const SportSearchPicker = ({
  open,
  title,
  sport,
  slotId,
  excludePlayerIds = [],
  onClose,
  onSelect,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searching = searchTerm.length >= 2;
  const skipSearch = !open || !searching;
  const nbaSearch = useSearchNbaPlayersQuery(searchTerm, {
    skip: skipSearch || sport !== "nba",
  });
  const nflSearch = useSearchNflPlayersQuery(searchTerm, {
    skip: skipSearch || sport !== "nfl",
  });
  const nbaLeaders = useGetNbaPositionLeadersQuery(slotId, {
    skip: !open || !slotId || sport !== "nba",
    refetchOnMountOrArgChange: true,
  });
  const nflLeaders = useGetNflPositionLeadersQuery(slotId, {
    skip: !open || !slotId || sport !== "nfl",
    refetchOnMountOrArgChange: true,
  });
  const query = sport === "nfl" ? nflSearch : nbaSearch;
  const leadersQuery = sport === "nfl" ? nflLeaders : nbaLeaders;
  const leaders = leadersQuery.currentData;

  useEffect(() => {
    if (!open) {
      setInputValue("");
      setSearchTerm("");
    }
  }, [open]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(inputValue.trim()), 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const excluded = useMemo(() => new Set(excludePlayerIds), [excludePlayerIds]);
  const options = (query.data || []).filter(
    (player) => !excluded.has(player.playerId)
  );
  const suggestions = (leaders?.players || []).filter(
    (player) => !excluded.has(player.playerId)
  );
  const showSuggestions = Boolean(slotId) && !searching;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ overflow: "visible", pt: 1 }}>
        <Autocomplete
          options={options}
          getOptionLabel={(player) => playerSearchName(player) || ""}
          loading={query.isFetching}
          value={null}
          inputValue={inputValue}
          open={searching}
          onInputChange={(_event, value, reason) => {
            if (reason === "reset" || reason === "selectOption") return;
            setInputValue(value);
          }}
          onChange={(_event, player) => {
            if (!player) return;
            onSelect({
              playerId: player.playerId,
              name: playerSearchName(player),
            });
            setInputValue("");
            setSearchTerm("");
          }}
          isOptionEqualToValue={(option, value) =>
            option?.playerId === value?.playerId
          }
          filterOptions={(items) => items}
          noOptionsText={
            searching
              ? "No players found"
              : "Type at least 2 letters to search everyone"
          }
          renderOption={(props, option) => {
            const { key, ...rowProps } = props;
            const nameMeta = [option?.bio?.team, option?.bio?.position]
              .filter(Boolean)
              .join(" · ");
            return (
              <Box component="li" key={option.playerId || key} {...rowProps}>
                <PlayerPickRow
                  player={option}
                  sport={sport}
                  meta={nameMeta}
                />
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Search the full player database"
              margin="dense"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {query.isFetching ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {showSuggestions && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: "0.12em" }}
            >
              Top at this position
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              Notable names here — or search everyone above.
            </Typography>
            {leadersQuery.isFetching && !leaders ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              suggestions.map((player) => (
                <PlayerPickRow
                  key={player.playerId}
                  player={player}
                  sport={sport}
                  meta={formatLeaderMeta(leaders?.metric, player.value)}
                  onClick={onSelect}
                />
              ))
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SportPlayerPicker = ({
  open,
  sport,
  title,
  slotId,
  excludePlayerIds,
  onClose,
  onSelect,
}) => {
  if (sport === "mlb") {
    return (
      <PlayerPickerDialog
        open={open}
        title={title}
        slotId={slotId}
        excludePlayerIds={excludePlayerIds}
        onClose={onClose}
        onSelect={onSelect}
      />
    );
  }

  return (
    <SportSearchPicker
      open={open}
      title={title}
      sport={sport}
      slotId={slotId}
      excludePlayerIds={excludePlayerIds}
      onClose={onClose}
      onSelect={onSelect}
    />
  );
};

export default SportPlayerPicker;
